import React, { useState, useEffect, useRef } from 'react';
import { FileText, X, Upload, Paperclip, Star } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'react-toastify';

interface ProfessionalInvoiceViewerProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string;
  user: any;
}

interface PaymentRecord {
  id: string;
  amount: number;
  paymentType: string;
  paymentProofUrl?: string;
  uploadedAt: string;
  fileName?: string;
  fileSize?: number;
  notes?: string;
  uploader: {
    name: string;
  };
}

interface InvoiceAttachment {
  id: string;
  filename: string;
  originalName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  type: 'PAYMENT_PROOF' | 'SUPPORTING_DOCUMENT' | 'CONTRACT' | 'OTHER';
}

interface InvoicePdfInfo {
  invoiceId: string;
  invoiceNumber: string;
  poId: string;
  status: string;
  amount: number;
  total: number;
  totalPaid: number;
  remainingBalance: number;
  createdAt: string;
  dueDate: string;
  hasPaymentProof: boolean;
  paymentRecords: PaymentRecord[];
  attachments?: InvoiceAttachment[];
  buyer: {
    name: string;
    email: string;
  };
  vendor: {
    name: string;
    email: string;
  };
}

const ProfessionalInvoiceViewer: React.FC<ProfessionalInvoiceViewerProps> = ({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  user
}) => {
  const { accessToken } = useAuthStore();
  const [pdfInfo, setPdfInfo] = useState<InvoicePdfInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentProofUpload, setShowPaymentProofUpload] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<InvoiceAttachment | null>(null);
  const [attachments, setAttachments] = useState<InvoiceAttachment[]>([]);
  const [selectedPaymentRecord, setSelectedPaymentRecord] = useState<PaymentRecord | null>(null);
  const [paymentType, setPaymentType] = useState<'FULL' | 'PARTIAL'>('FULL');
  const [partialAmount, setPartialAmount] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isOpen && invoiceId) {
      loadPdfInfo();
      loadInitialPdf();
    }
  }, [isOpen, invoiceId]);

  const loadPdfInfo = async () => {
    try {
      setIsLoading(true);
      console.log('Loading PDF info for invoiceId:', invoiceId);
      
      // Check authentication state
      console.log('API service instance:', apiService);
      console.log('Request URL:', `/payment/invoice/${invoiceId}/pdf/info`);
      
      // Temporary fix: Use full backend URL directly
      const backendUrl = 'https://evoltbay-production-production.up.railway.app/api/payment/invoice/' + invoiceId + '/pdf/info';
      console.log('Using direct backend URL:', backendUrl);
      const response = await apiService.get(backendUrl);
      console.log('PDF info response type:', typeof response);
      console.log('PDF info response:', response);
      
      if (response && typeof response === 'object' && (response as any).data) {
        const pdfData = (response as any).data;
        setPdfInfo(pdfData);
        if (pdfData.attachments) {
          setAttachments(pdfData.attachments);
        }
        console.log('PDF info set successfully:', pdfData);
      } else {
        console.error('Invalid response format - expected object with data property:', response);
        console.error('Response type:', typeof response);
        toast.error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error loading PDF info - full error:', error);
      console.error('Error response:', (error as any).response);
      console.error('Error status:', (error as any).response?.status);
      console.error('Error data:', (error as any).response?.data);
      toast.error('Failed to load invoice information');
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialPdf = async () => {
    try {
      const pdfUrl = await getPdfUrl(false);
      setCurrentPdfUrl(pdfUrl);
    } catch (error) {
      console.error('Error loading initial PDF:', error);
    }
  };

  const getPdfUrl = async (includePaymentProof: boolean = false): Promise<string> => {
      const directBackendUrl = `https://evoltbay-production-production.up.railway.app/api/payment/invoice/${invoiceId}/pdf/preview?includePaymentProof=${includePaymentProof}`;
      
      if (!accessToken) {
      throw new Error('No authentication token found');
      }

      const response = await fetch(directBackendUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const pdfBlob = await response.blob();
    return window.URL.createObjectURL(pdfBlob);
  };

  const handleMainInvoiceClick = async () => {
    try {
      setIsLoading(true);
      setSelectedAttachment(null); // Reset to show main invoice
      setSelectedPaymentRecord(null); // Reset payment record selection
      setZoomLevel(1); // Reset zoom level
      
      const pdfUrl = await getPdfUrl(false); // Load main invoice without payment proof
      setCurrentPdfUrl(pdfUrl);
      
      toast.success(`Loaded original invoice`);
    } catch (error) {
      console.error('Error loading main invoice:', error);
      toast.error('Failed to load main invoice');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentRecordClick = async (paymentRecord: PaymentRecord) => {
    try {
      setIsLoading(true);
      setSelectedAttachment(null);
      setSelectedPaymentRecord(paymentRecord);
      setZoomLevel(1); // Reset zoom level
      
      if (paymentRecord.paymentProofUrl) {
        // Load the specific payment proof PDF via backend proxy (avoids CORS issues)
        const backendUrl = `${import.meta.env.VITE_API_URL || 'https://api-service-production-eqdc.up.railway.app'}/api/payment/proof-file/${paymentRecord.id}`;
        
        const response = await fetch(backendUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setCurrentPdfUrl(url);
          toast.success(`Loaded payment proof #${(pdfInfo?.paymentRecords?.findIndex(p => p.id === paymentRecord.id) || 0) + 1}`);
        } else {
          throw new Error('Failed to load payment proof');
        }
      } else {
        toast.error('No payment proof file available');
      }
    } catch (error) {
      console.error('Error loading payment proof:', error);
      toast.error('Failed to load payment proof');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttachmentClick = async (attachment: InvoiceAttachment) => {
    try {
      setIsLoading(true);
      setSelectedAttachment(attachment);
      setSelectedPaymentRecord(null); // Reset payment record selection
      setZoomLevel(1); // Reset zoom level
      
      // For now, we'll load the main invoice PDF when clicking attachments
      // In a real implementation, you'd fetch the specific attachment
      const pdfUrl = await getPdfUrl(attachment.type === 'PAYMENT_PROOF');
      setCurrentPdfUrl(pdfUrl);
      
      toast.success(`Loaded ${attachment.originalName}`);
    } catch (error) {
      console.error('Error loading attachment:', error);
      toast.error('Failed to load attachment');
    } finally {
      setIsLoading(false);
    }
  };





  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handlePaymentProofUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    // Validate partial payment amount
    if (paymentType === 'PARTIAL') {
      if (!partialAmount || parseFloat(partialAmount) <= 0) {
        toast.error('Please enter a valid payment amount');
        return;
      }
      if (pdfInfo && parseFloat(partialAmount) > (pdfInfo.total / 100)) {
        toast.error('Payment amount cannot exceed invoice total');
        return;
      }
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('paymentProof', selectedFile);
      formData.append('invoiceId', invoiceId);
      formData.append('paymentType', paymentType);
      if (paymentType === 'PARTIAL') {
        formData.append('paymentAmount', (parseFloat(partialAmount) * 100).toString()); // Convert to cents
      }

      await apiService.post('/api/payment/upload-proof', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${paymentType === 'FULL' ? 'Full' : 'Partial'} payment proof uploaded successfully`);
      setShowPaymentProofUpload(false);
      setSelectedFile(null);
      setPaymentType('FULL');
      setPartialAmount('');
      loadPdfInfo(); // Refresh to show updated payment proof status
    } catch (error) {
      console.error('Error uploading payment proof:', error);
      toast.error('Failed to upload payment proof');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] sm:max-h-[98vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">Professional Invoice Viewer</h2>
              <p className="text-blue-100">Invoice #{invoiceNumber}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content - Responsive Layout */}
        <div className="flex flex-col lg:flex-row h-[calc(95vh-120px)]">
          {/* Mobile Header with Sidebar Toggle */}
          <div className="lg:hidden bg-gray-50 border-b border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">Documents</h3>
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                {showMobileSidebar ? 'Hide' : 'Show'} Files
              </button>
            </div>
          </div>

          {/* Left Sidebar - Documents */}
          <div className={`${
            showMobileSidebar ? 'block' : 'hidden'
          } lg:block w-full lg:w-1/5 bg-gray-50 lg:border-r border-gray-200 p-3 lg:p-4 overflow-y-auto max-h-48 lg:max-h-none`}>
            {/* Original Invoice Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Original Invoice</h3>
              
          {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
              ) : (
                <div 
                  onClick={handleMainInvoiceClick}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-blue-50 ${
                    !selectedAttachment ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        📄 Invoice #{invoiceNumber}
                      </p>
                      <p className="text-xs text-gray-500">Main invoice document</p>
                  </div>
                    {!selectedAttachment && (
                      <div className="text-xs bg-blue-600 text-white px-2 py-1 rounded">
                        Current
                  </div>
                    )}
                  </div>
                </div>
              )}
              </div>

            {/* Payment Proof Section */}
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-800">Attached files for this invoice</h3>
              <p className="text-sm text-gray-600 mb-4">Payment proof documents uploaded by buyer</p>
              
              <div className="space-y-2">

                {/* Payment Records List */}
                {pdfInfo?.paymentRecords && pdfInfo.paymentRecords.length > 0 && (
                  pdfInfo.paymentRecords.map((payment, index) => (
                    <div 
                      key={payment.id}
                      onClick={() => handlePaymentRecordClick(payment)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-green-50 ${
                        selectedPaymentRecord?.id === payment.id ? 'bg-green-100 border-green-300' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Paperclip size={20} className="text-green-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            💳 Payment #{index + 1} - ${(payment.amount / 100).toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {payment.fileName || 'Payment proof'} • {payment.uploader.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(payment.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-xs bg-green-600 text-white px-2 py-1 rounded">
                          ✓ PAID
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Additional Payment Proof Attachments */}
                {attachments.map((attachment) => (
                  <div 
                    key={attachment.id}
                    onClick={() => handleAttachmentClick(attachment)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-gray-100 ${
                      selectedAttachment?.id === attachment.id ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Paperclip size={20} className="text-orange-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          💰 {attachment.originalName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(attachment.fileSize / 1024).toFixed(1)} KB • Payment proof
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(attachment.uploadedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-xs bg-orange-600 text-white px-2 py-1 rounded">
                        Proof
                </div>
                    </div>
                  </div>
                ))}

                {/* No Payment Proofs Yet */}
                {(!pdfInfo?.paymentRecords || pdfInfo.paymentRecords.length === 0) && attachments.length === 0 && (
                  <div className="mt-4 p-4 bg-gray-100 border border-gray-300 rounded-lg text-center">
                    <Paperclip size={24} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">No payment proofs uploaded yet</p>
                    <p className="text-xs text-gray-500 mt-1">Buyer needs to upload payment evidence</p>
                  </div>
                )}

                {/* Upload Section for Enterprise Buyers - Only show if invoice needs more payment */}
                {((user?.role === 'BUYER' && user?.isEnterprise) || user?.role === 'ENTERPRISE_BUYER') && 
                 (pdfInfo?.status === 'PENDING' || pdfInfo?.status === 'OVERDUE' || pdfInfo?.status === 'PARTIALLY_PAID') && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-semibold mb-2 text-blue-900">📤 Upload Payment Proof</h4>
                    <p className="text-xs text-gray-600 mb-3">Upload evidence of payment to mark invoice as paid/partially paid</p>
                    
                    {/* Payment Type Selection */}
                    <div className="mb-3">
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Payment Type</label>
                      <select 
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value as 'FULL' | 'PARTIAL')}
                        className="w-full text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                      >
                                                    <option value="FULL">Full Payment - ${pdfInfo ? (pdfInfo.remainingBalance / 100).toFixed(2) : '0.00'}</option>
                        <option value="PARTIAL">Partial Payment</option>
                      </select>
              </div>

                    {/* Partial Amount Input */}
                    {paymentType === 'PARTIAL' && (
                      <div className="mb-3">
                        <label className="text-xs font-medium text-gray-700 mb-1 block">Payment Amount</label>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-500 mr-1">$</span>
                          <input
                            type="number"
                            value={partialAmount}
                            onChange={(e) => setPartialAmount(e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            max={pdfInfo ? (pdfInfo.remainingBalance / 100) : 0}
                            className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
                          />
                          <span className="text-xs text-gray-500 ml-1">of ${pdfInfo ? (pdfInfo.remainingBalance / 100).toFixed(2) : '0.00'}</span>
                        </div>
                      </div>
                    )}
                  {!showPaymentProofUpload ? (
                    <button
                      onClick={() => setShowPaymentProofUpload(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm transition-colors"
                    >
                        <Upload size={14} />
                        Upload File
                    </button>
                  ) : (
                      <div className="space-y-3">
                        <input
                          type="file"
                          onChange={handleFileSelect}
                          accept=".pdf,.jpg,.jpeg,.png,.gif"
                          className="w-full text-sm"
                        />
                      <div className="flex gap-2">
                        <button
                          onClick={handlePaymentProofUpload}
                            disabled={!selectedFile || isUploading || (paymentType === 'PARTIAL' && (!partialAmount || parseFloat(partialAmount) <= 0))}
                            className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
                        >
                            <Upload size={12} />
                            {isUploading ? 'Uploading...' : `Upload ${paymentType === 'FULL' ? 'Full' : 'Partial'} Payment`}
                        </button>
                        <button
                          onClick={() => {
                            setShowPaymentProofUpload(false);
                            setSelectedFile(null);
                              setPaymentType('FULL');
                              setPartialAmount('');
                          }}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

                {/* Invoice Fully Paid Message */}
                {((user?.role === 'BUYER' && user?.isEnterprise) || user?.role === 'ENTERPRISE_BUYER') && 
                 pdfInfo?.status === 'PAID' && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-2xl">✅</span>
                      <h4 className="text-sm font-semibold text-green-900">Invoice Fully Paid</h4>
                    </div>
                    <p className="text-xs text-green-700">This invoice has been fully paid. No additional payment proof uploads are needed.</p>
                    <p className="text-xs text-green-600 mt-1">
                      Invoice Total: ${(pdfInfo.total / 100).toFixed(2)} | 
                      Payments Made: {pdfInfo.paymentRecords?.length || 0} | 
                      Status: Fully Paid ✅
                    </p>
                    
                    {/* Rate Vendor Button */}
                    <div className="mt-4">
                      <button
                        onClick={() => window.open('/enterprise/post-quote-activity/feedback', '_blank')}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Star size={16} />
                        Rate Vendor
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center - PDF Viewer */}
          <div className="flex-1 lg:w-3/5 bg-white flex flex-col min-h-0">
            {/* PDF Header */}
            {currentPdfUrl && (
              <div className="bg-gray-100 px-3 lg:px-4 py-2 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedPaymentRecord ? (
                      <>
                        <Paperclip size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                          Payment #{(pdfInfo?.paymentRecords?.findIndex(p => p.id === selectedPaymentRecord.id) || 0) + 1} - ${(selectedPaymentRecord.amount / 100).toFixed(2)}
                        </span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex-shrink-0">Payment Proof</span>
                      </>
                    ) : !selectedAttachment ? (
                      <>
                        <FileText size={16} className="text-blue-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">Original Invoice #{invoiceNumber}</span>
                      </>
                    ) : (
                      <>
                        <Paperclip size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">{selectedAttachment.originalName}</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex-shrink-0">Payment Proof</span>
                      </>
                    )}
                  </div>
                  {/* PDF Controls */}
                  <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                    <button
                      onClick={() => setZoomLevel(Math.min(zoomLevel + 0.25, 2))}
                      className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                      title="Zoom In"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setZoomLevel(Math.max(zoomLevel - 0.25, 0.5))}
                      className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                      title="Zoom Out"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                      </svg>
                    </button>

                    <button
                      onClick={() => setZoomLevel(1)}
                      className="text-xs text-blue-600 hover:text-blue-800 hidden sm:inline px-1"
                      title="Reset Zoom (100%)"
                    >
                      {Math.round(zoomLevel * 100)}%
                    </button>

                    <button
                      onClick={() => {
                        if (iframeRef.current?.contentWindow) {
                          iframeRef.current.contentWindow.print();
                        } else {
                          // Fallback: open PDF in new tab for printing
                          window.open(currentPdfUrl, '_blank');
                        }
                      }}
                      className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                      title="Print PDF"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = currentPdfUrl;
                        link.download = `invoice-${invoiceNumber}.pdf`;
                        link.click();
                      }}
                      className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                      title="Download PDF"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => {
                        // Open PDF in new tab to access full browser PDF viewer with zoom controls
                        window.open(currentPdfUrl, '_blank');
                      }}
                      className="p-1 sm:p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
                      title="Open in new tab (full controls)"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>

                    <span className="text-xs text-gray-500 hidden sm:inline">
                    {!selectedAttachment ? 'Default view' : 'Uploaded by buyer'}
                    </span>
                  </div>
                </div>

                {/* Mobile Invoice Info */}
                {pdfInfo && (
                  <div className="lg:hidden mt-2 pt-2 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-gray-600">Amount:</span>
                        <span className="ml-1 font-medium">${(pdfInfo.amount / 100).toFixed(2)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <span className={`ml-1 px-2 py-1 rounded text-xs font-medium ${
                          pdfInfo.status === 'PAID' ? 'bg-green-100 text-green-800' :
                          pdfInfo.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {pdfInfo.status}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Due:</span>
                        <span className="ml-1">{new Date(pdfInfo.dueDate).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total:</span>
                        <span className="ml-1 font-bold">${(pdfInfo.total / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* PDF Content */}
            {currentPdfUrl ? (
              <div className="flex-1 min-h-0 overflow-auto">
                <div
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'top left',
                    width: `${100 / zoomLevel}%`,
                    height: `${100 / zoomLevel}%`,
                    minHeight: '400px'
                  }}
                >
              <iframe
                ref={iframeRef}
                src={currentPdfUrl}
                className="w-full h-full border-0"
                title={!selectedAttachment ? "Invoice PDF" : "Payment Proof Document"}
                    style={{ minHeight: '400px' }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-100 rounded-b-lg lg:rounded-none">
                <div className="text-center p-8">
                  <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">Loading invoice...</p>
                  {isLoading && (
                    <div className="mt-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  )}
                </div>
            </div>
          )}
          </div>

          {/* Right Sidebar - Invoice Info */}
          <div className="hidden xl:block w-1/5 bg-gray-50 border-l border-gray-200 p-4 overflow-y-auto">
            <div className="space-y-4">
              {/* Payment Status & Invoice Info */}
              {pdfInfo && (
                <div className="mt-6 space-y-4">
                  {/* Payment Status */}
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="text-sm font-semibold mb-3 text-gray-800">💳 Payment Status</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Status:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          pdfInfo.status === 'PAID' ? 'bg-green-100 text-green-800' :
                          pdfInfo.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {pdfInfo.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Payment Proof:</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          pdfInfo.hasPaymentProof ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {pdfInfo.hasPaymentProof ? '✓ Uploaded' : '✗ Missing'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="p-4 bg-white rounded-lg border">
                    <h4 className="text-sm font-semibold mb-3 text-gray-800">📄 Invoice Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Amount:</span>
                        <span className="font-medium">${(pdfInfo.amount / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total:</span>
                        <span className="font-bold text-lg">${(pdfInfo.total / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Due Date:</span>
                        <span className="text-xs">{new Date(pdfInfo.dueDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 border-t">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Professional invoice generated by EvoltBay
            </p>
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors w-full sm:w-auto"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalInvoiceViewer; 